import Tagify from '@yaireo/tagify';
import debounce from 'lodash/debounce';
import { buildSafeURLSearchParams, fetchWithTimeout } from '../api_utils';
import { escapeHTML, decodeHTML } from '../utils';
import { bindMentionAvatarErrorHandler } from '../utils/mention_avatar_fallback';

/**
 * FIXME: category inputとsegment group inputのselectorのscopeをもっと狭くしたい
 * なぜなら、mention inputは
 * 投稿の作成、修正画面だけでなく、詳細画面で複数入力ファームが表示されるケースがあるため。
 */
class Mention {
  constructor($elem) {
    this.$elem = $elem;
    const tagify = this.$elem?.__tagify;

    this.initialValues = this.compactValues();

    // 既にtagifyのインスタンスがある場合にはリセット
    if (tagify) {
      tagify.destroy();
    }
    this.$tagify = this.initTagify(this.$elem);
    bindMentionAvatarErrorHandler();

    // category input
    const categorySelect = {
      announcement: document.querySelector('[name="announcement_category_id"]'),
      chat: document.querySelector('[name="chat_category_id"]'),
      question: document.querySelector('[name="question_category_id"]'),
    };
    this.$categoryInputElem = categorySelect?.[this.$elem.dataset.postableType];

    // segment group input
    this.$segmentInputElem = document.querySelector(
      '[name="announcement[post_segment_group_segment_group_id]"].js-segment-group-select'
    );

    // add listeners
    this.boundCategoryInputChange = this.handleCategoryInputChange.bind(this);
    this.boundSegmentInputChange = this.handleSegmentInputChange.bind(this);
    this.addEventListeners();
  }

  /**
   * segment group selectの値が変更されたときに呼ばれる
   * すでにmentionを選択がある場合には、mentionをリセットする。
   */
  async handleSegmentInputChange() {
    await this.changeCategory(this.$tagify);
  }

  /**
   * category selectの値が変更されたときに呼ばれる
   */
  async handleCategoryInputChange() {
    await this.changeCategory(this.$tagify);
  }

  addEventListeners() {
    // カテゴリーを変更した際に選択ユーザーとホワイトリストを作り直す
    if (this.$categoryInputElem) {
      this.$categoryInputElem.addEventListener(
        'change',
        this.boundCategoryInputChange
      );
    }
    // セグメントグループを変更した際にユーザーとホワイトリストを作り直す
    if (this.$segmentInputElem) {
      this.$segmentInputElem.addEventListener(
        'change',
        this.boundSegmentInputChange
      );
    }
  }

  /**
   * 現在は使ってないが、reactと兼用で使う場合のために作る
   */
  removeEventListeners() {
    if (this.$categoryInputElem) {
      this.$categoryInputElem.removeEventListener(
        'change',
        this.boundCategoryInputChange
      );
    }
    if (this.$segmentInputElem) {
      this.$segmentInputElem.removeEventListener(
        'change',
        this.boundSegmentInputChange
      );
    }
  }

  // Override Wrapper
  // https://github.com/yairEO/tagify/blob/v4.13.3/src/parts/templates.js
  wrapperTemplate(input, _s) {
    return `<tags
              class="${_s.classNames.namespace} ${
      _s.mode ? `${_s.classNames[_s.mode + 'Mode']}` : ''
    }"
                  ${_s.readonly ? 'readonly' : ''}
                  ${_s.disabled ? 'disabled' : ''}
                  ${_s.required ? 'required' : ''}
                  ${_s.mode === 'select' ? "spellcheck='false'" : ''}
                  tabIndex="-1">
          <span ${
            !_s.readonly && _s.userInput ? 'contenteditable' : ''
          } tabIndex="0" data-placeholder="${
      _s.placeholder || '&#8203;'
    }" aria-placeholder="${_s.placeholder || ''}"
              class="${_s.classNames.input}"
              role="textbox"
              aria-autocomplete="both"
              aria-multiline="${_s.mode === 'mix'}"></span>
              &#8203;
      </tags>`;
  }

  tagTemplate(tagData) {
    const nickname = tagData.nickname ? escapeHTML(decodeHTML(tagData.nickname)) : null;
    return `<tag
              title="${nickname}"
              contenteditable="false"
              spellcheck="false"
              tabIndex="-1"
              class="tagify__tag"
            >
              <x
                title=""
                class="tagify__tag__removeBtn"
                role="button"
                aria-label="remove tag"
              ></x>
              <div class="tagify__tag__wrap">
                <div class="tagify__tag__avatar">
                  <img class="js-mention-avatar" data-fallback="${escapeHTML(tagData.avatarFallback || '')}" src="${escapeHTML(tagData.avatar)}">
                </div>
                <span class="tagify__tag-text">${nickname}</span>
              </div>
            </tag>
          `;
  }

  dropdownItemTemplate(tagData) {
    const nickname = tagData.nickname ? escapeHTML(tagData.nickname) : null;
    return `<div
              ${this.getAttributes(tagData)}
              class="tagify__dropdown__item"
              tabindex="0"
              role="option"
            >
              ${
                tagData.avatar
                  ? `
              <div class="tagify__dropdown__item__avatar">
                <img
                  class="tagify__dropdown__item__img js-mention-avatar"
                  data-fallback="${escapeHTML(tagData.avatarFallback || '')}"
                  src="${escapeHTML(tagData.avatar)}"
                >
              </div>`
                  : ''
              }
              <div class="tagify__dropdown__item-info">
                <div class="tagify__dropdown__item-name">${nickname}</div>
                ${
                  tagData.username !== null
                    ? `
                <dl class="tagify__dropdown__item-id">
                  <dt>ID</dt>
                  <dd>${escapeHTML(tagData.username)}</dd>
                </dl>`
                    : ''
                }
              </div>
            </div>
          `;
  }

  // 選択肢が表示しきれない場合の「○○ more items. Refine your search.」というメッセージを抑止する
  dropdownFooter(suggestions) {
    return '';
  }

  convertResponse(response) {
    const { users, username_displayed, default_avatar_url } = response;
    if (!users || users.length < 1) {
      return [];
    }

    return users.map(
      ({ nickname, avatar_url_or_default, url_name, username }, _) => {
        return {
          nickname,
          username: username_displayed ? username : null,
          avatar: avatar_url_or_default,
          avatarFallback: default_avatar_url,
          value: url_name,
        };
      }
    );
  }

  async changeCategory(tagify) {
    try {
      if (tagify?.value.length > 0) {
        tagify.loading(true);

        const values = tagify.value;
        // 許可リストを更新し、選択済みを削除
        tagify.whitelist = await this.suggest(
          null,
          values.map((inputedUser) => inputedUser.value)
        );

        tagify.removeAllTags();
        // 選択済みのユーザーを追加、許可リストにないユーザーは追加されない
        tagify.addTags(values);
        tagify.loading(false);
      } else {
        tagify.loading(true);
        tagify.whitelist = [];
        tagify.removeAllTags();
        tagify.loading(false);
      }
    } catch (e) {
      console.error('error: ', e);
    }
  }

  getCategoryId() {
    if (this.$categoryInputElem) {
      return this.$categoryInputElem.value;
    }

    const dataset = this.$elem.dataset;
    return dataset.categoryId;
  }

  getSegmentGroupId() {
    let segment_group_id = undefined;
    if (this.$segmentInputElem) {
      segment_group_id = this.$segmentInputElem.value;
    }
    return segment_group_id;
  }

  async suggest(word = null, url_name = null) {
    const dataset = this.$elem.dataset;
    const paramsObj = {
      word: word,
      url_name: url_name,
      category_id: this.getCategoryId(),
      segment_group_id: this.getSegmentGroupId(),
      is_root_post: dataset.isRootPost,
      is_params_preferred: dataset.isParamsPreferred,
      menu_id: dataset.menuId,
      postable_type: dataset.postableType,
      postable_id: dataset.postableId,
      persisted: dataset.persisted,
      parent_type: dataset.parentType,
      parent_id: dataset.parentId,
    };
    const searchParams = buildSafeURLSearchParams(paramsObj);
    const response = await fetchWithTimeout(
      `/mentions/suggest?${searchParams.toString()}`,
      {
        method: 'GET',
      }
    );
    const responseJson = await response.json();
    return this.convertResponse(responseJson);
  }

  /**
   * Tagify は whitelist が空の状態で初期化するため、元の input の値は初期化直後に空文字になる。
   * このまま送信されると mention_list が空になり、サーバー側で既存メンションがすべて削除される。
   * 読み込みが終わるまでは元の値を持つ hidden input に name を移して送信値を保つ。
   * 読み込みに失敗した場合は hidden input を残し、既存メンションを維持する。
   * @param {string[]} initialValues
   * @returns {() => void} 読み込み完了後に name を元の input に戻す関数
   */
  keepSubmitValueUntilLoaded(initialValues) {
    const name = this.$elem?.name;
    if (!name) return () => {};

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = name;
    hidden.value = initialValues.join(',');
    this.$elem.removeAttribute('name');
    this.$elem.insertAdjacentElement('afterend', hidden);

    return () => {
      hidden.remove();
      this.$elem.name = name;
    };
  }

  compactValues() {
    return this.$elem?.value
      .split(',')
      .filter((x) => !(x === null || x === undefined || x === ''));
  }

  async updateAllowlist(tagify, addedUsers = []) {
    const diffAllowlist = addedUsers.filter(
      (addedUser) =>
        !tagify.whitelist.some(
          (allowlistUser) => allowlistUser.value == addedUser.value
        )
    );

    // プリロードされたリストにいないユーザーがあればホワイトリストに追加
    if (diffAllowlist.length > 0) {
      tagify.whitelist = tagify.whitelist.concat(diffAllowlist);
    }
  }

  initTagify(elem) {
    const tagify = new Tagify(elem, {
      tagTextProp: 'nickname',
      enforceWhitelist: true,
      skipInvalid: true,
      editTags: false,
      pasteAsTags: false,
      dropdown: {
        maxItems: 50,
        closeOnSelect: false,
        enabled: 0,
        classname: 'users-list',
        searchKeys: ['nickname', 'username'],
      },
      templates: {
        wrapper: this.wrapperTemplate,
        tag: this.tagTemplate,
        dropdownItem: this.dropdownItemTemplate,
        dropdownFooter: this.dropdownFooter,
      },
      whitelist: [],
      originalInputValueFormat: (values) =>
        values.map((item) => item.value).join(','),
      validate: ({ value }) =>
        tagify.whitelist.some((data) => value === data.value),
      hooks: {
        beforePaste: (e, { tagify, pastedText, _clipboardData }) => {
          const searchUsers = async () => {
            const addedUsers = await this.suggest(pastedText);
            await this.updateAllowlist(tagify, addedUsers);

            // ドロップダウンの表示
            tagify.loading(false);
            tagify.dropdown.show();
          };

          searchUsers();

          return Promise.resolve();
        },
      },
    });

    return tagify;
  }

  async setEvents() {
    const tagify = this.$tagify;

    let isCompositionStart = false;
    const initialValues = this.initialValues;

    // dropdownのDOMポジションコントロールをOverride
    tagify.dropdown.position = this.position.bind(tagify);
    // onKeyDownイベントに含まれるIMEバグに対するOverride
    tagify.dropdown.events.callbacks.onKeyDown = this.onKeyDown.bind(tagify);

    // 編集時などの既に値がある場合
    if (initialValues.length > 0) {
      // 読み込み完了までは元の値を送信する（完了前に更新されても既存メンションを消さない）
      const restoreSubmitValue = this.keepSubmitValueUntilLoaded(initialValues);

      // 既に追加されているユーザーと、その差分を取得
      tagify.loading(true);

      const addedUsers = await this.suggest(null, initialValues);
      await this.updateAllowlist(tagify, addedUsers);

      tagify.addTags(addedUsers);
      tagify.loading(false);
      restoreSubmitValue();
    }

    // IME用文字列変換開始
    tagify.DOM.input.addEventListener('compositionstart', (e) => {
      isCompositionStart = true;
    });

    // IME用文字列変換完了
    tagify.DOM.input.addEventListener('compositionend', async (e) => {
      isCompositionStart = false;

      const currentInputValue = (tagify.DOM.input.innerText || '').trim();

      if (!currentInputValue) return;

      tagify.loading(true);

      const addedUsers = await this.suggest(currentInputValue);
      await this.updateAllowlist(tagify, addedUsers);

      tagify.loading(false);
      tagify.dropdown.show();
    });

    tagify
      .on('remove', () => {
        isCompositionStart = false;
      })
      .on(
        'input',
        debounce(async (e) => {
          if (!e.detail.value) {
            return false;
          }
          // FirefoxでもIEでもなく、且つ半角英数の時にはトリガーしない
          if (
            !tagify.isFirefox &&
            !tagify.isIE &&
            (!e.detail.value.match(/^[ -~]*$/) || isCompositionStart)
          ) {
            return false;
          }

          tagify.loading(true);

          const addedUsers = await this.suggest(e.detail.value);
          await this.updateAllowlist(tagify, addedUsers);

          tagify.loading(false);
          tagify.dropdown.show();
        }, 200)
      );
  }

  // Tagify.Dropdownの表示位置をコントロールするMethodのMonkeyPatch
  // https://github.com/yairEO/tagify/blob/v4.13.3/dist/tagify.js
  position(ddHeightParam) {
    var _sd = this.settings.dropdown;
    if (_sd.position === 'manual') return;
    var rect,
      parentRect,
      top,
      bottom,
      left,
      width,
      parentsPositions,
      ddElm = this.DOM.dropdown,
      placeAbove = _sd.placeAbove,
      viewportHeight = document.documentElement.clientHeight,
      viewportWidth = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      ),
      positionTo = viewportWidth > 480 ? _sd.position : 'all',
      // 変更1: 上下の位置はデフォルトのscopeを基準に取得
      ddTarget = this.DOM.scope,
      // 変更2: 左右の位置はscope側が横スクロールを許容することで取得できない為、
      // 横スクロールを許容している祖先要素（スクロールされても位置が動かない箱）から取得する。
      // scope.parentNodeを決め打ちにすると、Reactマウント用のラッパーdiv等が
      // scopeとスクロールコンテナの間に挟まった場合にスクロール量ぶんズレて表示されてしまう
      // （実例: COMMUNITY-4689 メンション入力のReact化でラッパーdivが挟まり発生）。
      ddTargetParent = getScrollAncestor(this.DOM.scope),
      ddHeight = ddHeightParam || ddElm.clientHeight;

    function getParentsPositions(p) {
      var left = 0,
        top = 0;

      while (p) {
        left += p.offsetLeft || 0;
        top += p.offsetTop || 0;
        p = p.parentNode;
      }

      return {
        left,
        top,
      };
    }

    // scopeの祖先を辿り、横スクロールを許容している（＝overflow-xがvisibleでない）
    // 直近の要素を返す。DOM構造の変更（Reactラッパーdivの挿入等）があっても、
    // 見た目上スクロールしない安定した箱を動的に見つけられるようにする。
    function getScrollAncestor(el) {
      var p = el.parentElement;
      while (p) {
        var overflowX = window.getComputedStyle(p).overflowX;
        if (overflowX && overflowX !== 'visible') return p;
        p = p.parentElement;
      }
      return el.parentNode;
    }

    if (!this.state.dropdown.visible) return;

    if (positionTo === 'text') {
      rect = this.getCaretGlobalPosition();
      bottom = rect.bottom;
      top = rect.top;
      left = rect.left;
      width = 'auto';
    } else {
      parentsPositions = getParentsPositions(
        this.settings.dropdown.appendTarget
      );
      rect = ddTarget.getBoundingClientRect();
      parentRect = ddTargetParent.getBoundingClientRect();
      top = rect.top - parentsPositions.top;
      bottom = rect.bottom - 1 - parentsPositions.top;
      left = parentRect.left - parentsPositions.left;
      width = parentRect.width + 'px';
    }

    top = Math.floor(top);
    bottom = Math.ceil(bottom);
    placeAbove =
      placeAbove === undefined
        ? viewportHeight - rect.bottom < ddHeight
        : placeAbove; // flip vertically if there is no space for the dropdown below the input

    ddElm.style.cssText =
      'left:' +
      (left + window.pageXOffset) +
      'px; width:' +
      width +
      ';' +
      (placeAbove
        ? 'top: ' + (top + window.pageYOffset) + 'px'
        : 'top: ' + (bottom + window.pageYOffset) + 'px');
    ddElm.setAttribute('placement', placeAbove ? 'top' : 'bottom');
    ddElm.setAttribute('position', positionTo);
  }

  // TagifyのIMEバグ修正Monkeypatch
  // Enter時に229だった場合、処理から離脱するように修正
  // https://github.com/yairEO/tagify/blob/v4.13.3/dist/tagify.js
  // https://github.com/yairEO/tagify/blob/master/src/parts/dropdown.js
  onKeyDown(e) {
    if (!this.state.hasFocus) return;

    // get the "active" element, and if there was none (yet) active, use first child
    var selectedElm = this.DOM.dropdown.querySelector(
        this.settings.classNames.dropdownItemActiveSelector
      ),
      selectedElmData = this.dropdown.getSuggestionDataByNode(selectedElm);

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Down': // >IE11
      case 'Up': {
        // >IE11
        e.preventDefault();
        var dropdownItems = this.dropdown.getAllSuggestionsRefs(),
          actionUp = e.key === 'ArrowUp' || e.key === 'Up';

        if (selectedElm) {
          selectedElm = this.dropdown.getNextOrPrevOption(
            selectedElm,
            !actionUp
          );
        }

        // if no element was found OR current item is not a "real" item, loop
        if (
          !selectedElm ||
          !selectedElm.matches(this.settings.classNames.dropdownItemSelector)
        ) {
          selectedElm = dropdownItems[actionUp ? dropdownItems.length - 1 : 0];
        }

        selectedElmData = this.dropdown.getSuggestionDataByNode(selectedElm);

        this.dropdown.highlightOption(selectedElm, true);
        // selectedElm.scrollIntoView({inline: 'nearest', behavior: 'smooth'})
        break;
      }
      case 'Escape':
      case 'Esc': // IE11
        this.dropdown.hide();
        break;

      case 'ArrowRight':
        if (this.state.actions.ArrowLeft) return;
      case 'Tab': {
        // in mix-mode, treat arrowRight like Enter key, so a tag will be created
        if (
          this.settings.mode !== 'mix' &&
          selectedElm &&
          !this.settings.autoComplete.rightKey &&
          !this.state.editing
        ) {
          e.preventDefault(); // prevents blur so the autocomplete suggestion will not become a tag
          var value = this.dropdown.getMappedValue(selectedElmData);

          this.input.autocomplete.set.call(this, value);
          return false;
        }
        return true;
      }
      case 'Enter': {
        // Monkeypatch-Start
        if (e.keyCode === 229) return;
        // Monkeypatch-End

        e.preventDefault();

        this.settings.hooks
          .suggestionClick(e, {
            tagify: this,
            tagData: selectedElmData,
            suggestionElm: selectedElm,
          })
          .then(() => {
            if (selectedElm) {
              this.dropdown.selectOption(selectedElm);
              // highlight next option
              selectedElm = this.dropdown.getNextOrPrevOption(
                selectedElm,
                !actionUp
              );
              this.dropdown.highlightOption(selectedElm);
              return;
            } else this.dropdown.hide();

            if (this.settings.mode !== 'mix')
              this.addTags(this.state.inputText.trim(), true);
          })
          .catch((err) => err);

        break;
      }
      case 'Backspace': {
        if (this.settings.mode === 'mix' || this.state.editing.scope) return;

        const value = this.input.raw.call(this);

        if (value === '' || value.charCodeAt(0) === 8203) {
          if (this.settings.backspace === true) this.removeTags();
          else if (this.settings.backspace === 'edit')
            setTimeout(this.editTag.bind(this), 0);
        }
      }
    }
  }
}

export default Mention;
